import { useState } from 'react';
import useAuthStore from '../store/authStore';
import AcquireOSLogo from '../components/AcquireOSLogo';
import '../styles/individual-setup.css';
import { syncContact } from '../services/ghlService';

export default function IndividualSetupPage({ onSignIn, onBack }) {
  const { sendRegistrationOTP, verifyRegistrationOTP, isLoading } = useAuthStore();

  const [form, setForm] = useState({
    name: '',
    email: '',
    strategy: '',
    dealSize: '',
    accredited: ''
  });
  const [errors, setErrors] = useState({});
  const [showPendingModal, setShowPendingModal] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpError, setOtpError] = useState('');

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = 'Full name is required';
    if (!form.email.trim()) errs.email = 'Email address is required';
    else if (!/\S+@\S+\.\S+/.test(form.email)) errs.email = 'Invalid email address';
    if (!form.strategy) errs.strategy = 'Please select an investment strategy';
    if (!form.dealSize) errs.dealSize = 'Please select a target deal size';
    if (!form.accredited) errs.accredited = 'Please select your accredited status';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setErrors({});
    const result = await sendRegistrationOTP({
      name: form.name,
      email: form.email,
      company: 'N/A',
      userType: 'individual',
      indStrategy: form.strategy,
      indDealSize: form.dealSize,
      indAccredited: form.accredited
    });
    if (!result.success) { setErrors({ general: result.error }); return; }
    setIsVerifyingOtp(true);
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (!otpCode.trim() || otpCode.length !== 6) {
      setOtpError('Please enter a 6-digit verification code');
      return;
    }
    setOtpError('');
    const result = await verifyRegistrationOTP(otpCode);
    if (!result.success) {
      setOtpError(result.error);
      return;
    }
    // Task 1: Sync contact to GHL on lead capture
    syncContact({ name: form.name, email: form.email, role: 'investor' }).catch(() => {});
    setIsVerifyingOtp(false);
    setShowPendingModal(true);
  };

  if (isVerifyingOtp) {
    return (
      <div className="is-root">
        <div className="is-card" style={{ maxWidth: 440 }}>
          <div className="is-logo"><AcquireOSLogo size={64} /></div>
          <h1 className="is-title">Verify Your Email</h1>
          <p className="is-sub" style={{ fontSize: '14px', color: '#475569', marginBottom: '24px', textAlign: 'center', lineHeight: '1.6' }}>
            We've sent a 6-digit verification code to <strong>{form.email}</strong>. Please enter the code below to complete your registration.
          </p>

          {otpError && <div className="is-error" style={{ marginBottom: '16px' }}>{otpError}</div>}

          <form onSubmit={handleVerifyOtp} className="is-form">
            <div className="is-field">
              <label className="is-floating-label">Verification Code</label>
              <input
                className="is-input"
                type="text"
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                maxLength={6}
                style={{ textAlign: 'center', fontSize: '20px', letterSpacing: '4px' }}
                autoFocus
              />
            </div>

            <button className="is-submit" type="submit" disabled={isLoading || otpCode.length !== 6}>
              {isLoading ? 'Verifying...' : 'Verify & Submit'}
            </button>
          </form>

          <button className="is-back" onClick={() => setIsVerifyingOtp(false)}>← Back to Sign Up</button>
        </div>
      </div>
    );
  }


  if (showPendingModal) {
    return (
      <div className="is-root">
        <div className="is-card" style={{ textAlign: 'center', padding: '40px 30px', maxWidth: 440 }}>
          <div style={{ fontSize: '50px', marginBottom: '20px' }}>⏳</div>
          <h1 className="is-title" style={{ fontSize: '24px', marginBottom: '12px' }}>Registration Submitted!</h1>
          <p className="is-sub" style={{ fontSize: '15px', color: '#475569', lineHeight: '1.6', marginBottom: '28px' }}>
            Thank you for registering, <strong>{form.name}</strong>. Your account request is now pending review by our platform administrators.
            <br /><br />
            You will receive a notification email as soon as your account status is updated.
          </p>
          <button 
            className="is-submit" 
            onClick={onSignIn}
            style={{ width: '100%' }}
          >
            Return to Sign In
          </button>
        </div>
      </div>
    );
  }

  // ── Main signup screen ───────────────────────────────
  return (
    <div className="is-root">
      <div className="is-card" style={{ maxWidth: 440 }}>
        <div className="is-logo"><AcquireOSLogo size={64} /></div>

        <h1 className="is-title">Sign up</h1>

        {errors.general && <div className="is-error">{errors.general}</div>}

        <form onSubmit={handleSignUp} className="is-form">
          {/* Name */}
          <div className={`is-field ${errors.name ? 'is-field--error' : ''}`}>
            <label className="is-floating-label">Name</label>
            <input
              className="is-input"
              type="text"
              name="name"
              value={form.name}
              onChange={handleChange}
              autoComplete="name"
              autoFocus
            />
            {errors.name && <span className="is-field-error">{errors.name}</span>}
          </div>

          {/* Email */}
          <div className={`is-field ${errors.email ? 'is-field--error' : ''}`}>
            <label className="is-floating-label">Email</label>
            <input
              className="is-input"
              type="email"
              name="email"
              value={form.email}
              onChange={handleChange}
              autoComplete="email"
            />
            {errors.email && <span className="is-field-error">{errors.email}</span>}
          </div>


          {/* Investment Strategy */}
          <div className={`is-field ${errors.strategy ? 'is-field--error' : ''}`}>
            <label className="is-floating-label">Investment Strategy</label>
            <select
              className="is-input"
              name="strategy"
              value={form.strategy}
              onChange={handleChange}
              style={{ appearance: 'none', background: '#f8fafc url("data:image/svg+xml;charset=UTF-8,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'24\' height=\'24\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%2364748b\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3E%3Cpolyline points=\'6 9 12 15 18 9\'%3E%3C/polyline%3E%3C/svg%3E") no-repeat right 12px center', backgroundSize: '16px' }}
            >
              <option value="">Select strategy...</option>
              <option value="Multifamily Value-Add">Multifamily Value-Add</option>
              <option value="Core Plus">Core Plus</option>
              <option value="Opportunistic">Opportunistic</option>
              <option value="Ground-up Development">Ground-up Development</option>
              <option value="Yield / Cash Flow">Yield / Cash Flow</option>
            </select>
            {errors.strategy && <span className="is-field-error">{errors.strategy}</span>}
          </div>

          {/* Target Deal Size */}
          <div className={`is-field ${errors.dealSize ? 'is-field--error' : ''}`}>
            <label className="is-floating-label">Target Deal Size</label>
            <select
              className="is-input"
              name="dealSize"
              value={form.dealSize}
              onChange={handleChange}
              style={{ appearance: 'none', background: '#f8fafc url("data:image/svg+xml;charset=UTF-8,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'24\' height=\'24\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%2364748b\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3E%3Cpolyline points=\'6 9 12 15 18 9\'%3E%3C/polyline%3E%3C/svg%3E") no-repeat right 12px center', backgroundSize: '16px' }}
            >
              <option value="">Select target size...</option>
              <option value="Under $5M">Under $5M</option>
              <option value="$5M - $20M">$5M - $20M</option>
              <option value="Over $20M">Over $20M</option>
            </select>
            {errors.dealSize && <span className="is-field-error">{errors.dealSize}</span>}
          </div>

          {/* Accredited Status */}
          <div className={`is-field ${errors.accredited ? 'is-field--error' : ''}`}>
            <label className="is-floating-label">Accredited Investor Status</label>
            <select
              className="is-input"
              name="accredited"
              value={form.accredited}
              onChange={handleChange}
              style={{ appearance: 'none', background: '#f8fafc url("data:image/svg+xml;charset=UTF-8,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'24\' height=\'24\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%2364748b\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3E%3Cpolyline points=\'6 9 12 15 18 9\'%3E%3C/polyline%3E%3C/svg%3E") no-repeat right 12px center', backgroundSize: '16px' }}
            >
              <option value="">Select status...</option>
              <option value="Accredited Investor">Accredited Investor</option>
              <option value="Non-Accredited Investor">Non-Accredited Investor</option>
            </select>
            {errors.accredited && <span className="is-field-error">{errors.accredited}</span>}
          </div>

          <button className="is-submit" type="submit" disabled={isLoading}>
            {isLoading ? 'Registering…' : 'Sign Up'}
          </button>
        </form>

        <p className="is-signin-link">
          Already have an account?{' '}
          <button className="is-link-btn" onClick={onSignIn}>Log In</button>
        </p>

        <button className="is-back" onClick={onBack}>← Back</button>
      </div>
    </div>
  );
}
